/**
 * ABHAPage — standalone ABHA management page at /org/abha
 *
 * Layout mirrors FeesBillingPage:
 *   title + subtitle → underline tab strip → white body card
 *
 * No ABHA slug at all → NoAccessGate.
 * Top-level tabs (shown only if the user has the permission):
 *   Create / Verify — Create (ABHA_CREATE) / Verify (ABHA_VERIFY) / Scan & Share (ABHA_SCAN_SHARE) sub-tabs,
 *                shown per slug held
 *   Find patient's ABHA (ABHA_LINK) — Patient mobile search → ABHA status
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import AdminPageHeader from '../components/common/AdminPageHeader';
import NoAccessGate from '../components/common/NoAccessGate';
import AbdmEnabledGate from '../components/common/AbdmEnabledGate';
import { NO_ACCESS_VARIANT } from '../components/common/NoAccessPanel';
import { ABDM_ANY_ABHA } from '../constants/abdmPermissions';
import ABHACreationFlow from '../components/abha/ABHACreationFlow';
import ABHAVerificationFlow from '../components/abha/ABHAVerificationFlow';
import ScanShareQueuePanel from '../components/abha/ScanShareQueuePanel';
import useScanQueueStore from '../stores/scanQueueStore';
import ScanShareCounterCards from '../components/abha/counters/ScanShareCounterCards';
import { usePermissions } from '../context/PermissionContext';
import { useTheme } from '../theme/ThemeContext';
import { getABHARecord } from '../services/api/abhaApi';
import { searchPatientByMobile } from '../services/api/visitApi';
import { toABHAProfileSnapshot, maskAbhaAddress } from '../services/api/abhaProfile';
import AbhaVerifiedResult from '../components/abha/AbhaVerifiedResult';
import { ROUTES } from '../constants/routes';
import { ADMISSION_PERMISSIONS } from '../constants/admission';
import './ABHAPage.css';
import { stashTransientAadhaar } from '../utils/transientAadhaar';
import { ABDM_ICONS, MODULE_TITLES, ABHA_PAGE, CREATE_ABHA, VERIFY_ABHA, SCAN_SHARE, ABHA_NUMBER_LABEL, ABHA_ADDRESS_LABEL } from '../constants/abdmCopy';
import AbhaBadge from '../components/visit/AbhaBadge';

const CARD_SUB_TABS = [
  { key: 'create', label: CREATE_ABHA, slug: 'ABHA_CREATE' },
  { key: 'verify', label: VERIFY_ABHA, slug: 'ABHA_VERIFY' },
  { key: 'scan',   label: SCAN_SHARE, slug: 'ABHA_SCAN_SHARE' },
];

export default function ABHAPage() {
  return (
    <NoAccessGate slug={ABDM_ANY_ABHA} deniedVariant={NO_ACCESS_VARIANT.ABHA}>
      <AbdmEnabledGate>
        <ABHAPageBody />
      </AbdmEnabledGate>
    </NoAccessGate>
  );
}

function ABHAPageBody() {
  const { hasPermission } = usePermissions();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const waiting = useScanQueueStore((s) => (s.entries ? s.entries.length : 0));

  // Creation already ends in its own "Continue to Registration" step, so completing it goes
  // straight through. Verify has no step of its own, so its result waits in a panel with a
  // continue button (disabled until the profile/ABHA number has loaded).
  const [completedSnapshot, setCompletedSnapshot] = useState(null);
  // The snapshot drops txnId; the verify-session card download needs it.
  const [completedTxnId, setCompletedTxnId] = useState('');
  // The plaintext Aadhaar never goes into router state (it would land in window.history.state, on
  // disk) -- it crosses to the register form through a one-shot in-memory hand-off instead.
  const goToRegistration = (snapshot) => {
    const { aadhaar, ...safeSnapshot } = snapshot;
    stashTransientAadhaar(aadhaar);
    navigate(ROUTES.ORG, { state: { openRegister: true, abhaSnapshot: safeSnapshot } });
  };
  const goToAdmission = (snapshot) => {
    const { aadhaar, ...safeSnapshot } = snapshot;
    stashTransientAadhaar(aadhaar);
    navigate(ROUTES.ADMIT_PATIENT, { state: { abhaSnapshot: safeSnapshot } });
  };
  const handleAbhaComplete = (raw, source) => {
    const snapshot = toABHAProfileSnapshot(raw, source);
    if (source === 'creation') {
      if (snapshot?.abhaNumber) goToRegistration(snapshot);
      return;
    }
    setCompletedTxnId(raw?.txnId || '');
    setCompletedSnapshot(snapshot);
  };
  const continueToRegistration = () => {
    if (!completedSnapshot?.abhaNumber) return;
    goToRegistration(completedSnapshot);
  };

  const continueToAdmission = () => {
    if (!completedSnapshot?.abhaNumber) return;
    goToAdmission(completedSnapshot);
  };

  const cardSubTabs = CARD_SUB_TABS.filter((t) => hasPermission(t.slug));
  const canCard = cardSubTabs.length > 0;
  const canLink = hasPermission('ABHA_LINK');
  const canCreate = hasPermission('ABHA_CREATE');
  const canVerify = hasPermission('ABHA_VERIFY');

  // Determine the default tab (the first one the user holds)
  const [topTab, setTopTab] = useState(canCard ? 'card' : 'link');
  // The dashboard's Scan & Share pill lands here with the tab already selected.
  const wantsScan = location?.state?.openTab === 'scan' && cardSubTabs.some((t) => t.key === 'scan');
  const [cardSubTab, setCardSubTab] = useState(wantsScan ? 'scan' : cardSubTabs[0]?.key);

  // ── ABHA Link section state ────────────────────────────────────────
  const [mobileInput, setMobileInput]   = useState('');
  const [linkSearching, setLinkSearching] = useState(false);
  const [linkPatients, setLinkPatients]   = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [abhaRecord, setAbhaRecord]     = useState(null);
  const [linkLoading, setLinkLoading]   = useState(false);
  const [linkError, setLinkError]       = useState('');

  const handleMobileSearch = async () => {
    const mobile = mobileInput.trim().replace(/\D/g, '');
    if (mobile.length !== 10) {
      setLinkError('Enter a valid 10-digit mobile number.');
      return;
    }
    setLinkError('');
    setLinkSearching(true);
    setLinkPatients(null);
    setSelectedPatient(null);
    setAbhaRecord(null);
    try {
      const patients = await searchPatientByMobile(mobile);
      setLinkPatients(patients);
      if (patients.length === 1) {
        await fetchABHARecord(patients[0]);
      }
    } catch {
      setLinkError('Patient lookup failed. Please try again.');
    } finally {
      setLinkSearching(false);
    }
  };

  const fetchABHARecord = async (patient) => {
    setSelectedPatient(patient);
    setAbhaRecord(null);
    setLinkLoading(true);
    setLinkError('');
    try {
      const rec = await getABHARecord({ patientId: patient.patient_id });
      setAbhaRecord(rec);
    } catch {
      setLinkError(ABHA_PAGE.findError);
    } finally {
      setLinkLoading(false);
    }
  };

  // "No ABHA on file" does not mean the patient has none - offer Verify first, Create second.
  const openCardTab = (key) => {
    setTopTab('card');
    setCardSubTab(key);
  };

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  return (
    <div className="abha-page" style={{ background: bg }}>
      <AdminPageHeader />

      <main className="abha-main">
        {/* Title row */}
        <div className="abha-title-row">
          <h1 className="abha-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
            {ABDM_ICONS.ABHA} {MODULE_TITLES.ABHA}
          </h1>
          <p className="abha-subtitle" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
            {ABHA_PAGE.subtitle}
          </p>
        </div>

        {/* Top-level tab strip (reuses fbt-* CSS from FeesBillingTabs) */}
        <div className="fbt-tabs" role="tablist">
          {canCard && (
            <button
              role="tab"
              aria-selected={topTab === 'card'}
              className={`fbt-tab ${topTab === 'card' ? 'fbt-tab-active' : ''}`}
              onClick={() => setTopTab('card')}
            >
              {ABHA_PAGE.tabCard}
            </button>
          )}
          {canLink && (
            <button
              role="tab"
              aria-selected={topTab === 'link'}
              className={`fbt-tab ${topTab === 'link' ? 'fbt-tab-active' : ''}`}
              onClick={() => setTopTab('link')}
            >
              {ABHA_PAGE.tabFind}
            </button>
          )}
        </div>

        {/* Body card */}
        <div className="abha-body" style={{
          background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
        }}>

          {/* ── ABHA Card tab ──────────────────────────────────── */}
          {topTab === 'card' && canCard && (
            <>
              {/* Sub-tab strip */}
              <div className="fbt-tabs abha-sub-tabs" role="tablist">
                {cardSubTabs.map((t) => (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={cardSubTab === t.key}
                    className={`fbt-tab ${cardSubTab === t.key ? 'fbt-tab-active' : ''}`}
                    onClick={() => setCardSubTab(t.key)}
                  >
                    {t.label}{t.key === 'scan' && waiting > 0 ? ` (${waiting})` : ''}
                  </button>
                ))}
              </div>

              {completedSnapshot && (
                <AbhaVerifiedResult
                  snapshot={completedSnapshot}
                  txnId={completedTxnId}
                  isDark={isDark}
                  onContinue={continueToRegistration}
                  onAdmit={hasPermission(ADMISSION_PERMISSIONS.ADMIT_PATIENT) ? continueToAdmission : undefined}
                  onStartOver={() => setCompletedSnapshot(null)}
                />
              )}

              {!completedSnapshot && cardSubTab === 'create' && (
                <ABHACreationFlow
                  onComplete={(raw) => handleAbhaComplete(raw, 'creation')}
                  onSkip={() => {}}
                  onSwitchToVerify={hasPermission('ABHA_VERIFY') ? () => setCardSubTab('verify') : undefined}
                />
              )}
              {!completedSnapshot && cardSubTab === 'verify' && (
                <ABHAVerificationFlow
                  onComplete={(raw) => handleAbhaComplete(raw, 'verification')}
                  onSkip={() => canCreate && setCardSubTab('create')}
                  onCreateAbha={canCreate ? () => setCardSubTab('create') : undefined}
                />
              )}
              {cardSubTab === 'scan' && hasPermission('ABHA_SCAN_SHARE') && (
                <>
                  <ScanShareQueuePanel />
                  <h3 className="mt-4 mb-2 text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">Counter QR codes</h3>
                  <ScanShareCounterCards onSetUpCounters={() => navigate(ROUTES.SETTINGS)} />
                </>
              )}
            </>
          )}

          {/* ── ABHA Link tab ───────────────────────────────────── */}
          {topTab === 'link' && canLink && (
            <div className="abha-link-body">
              <p className="abha-link-hint" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                {ABHA_PAGE.findHint}
              </p>

              {/* Mobile search row */}
              <div className="abha-link-search-row">
                <input
                  type="tel"
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  value={mobileInput}
                  onChange={(e) => setMobileInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  onKeyDown={(e) => e.key === 'Enter' && handleMobileSearch()}
                  className="abha-link-input"
                  style={{
                    background: isDark ? '#0f172a' : '#ffffff',
                    color: isDark ? '#f1f5f9' : '#111827',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                  }}
                />
                <button
                  onClick={handleMobileSearch}
                  disabled={linkSearching || mobileInput.length < 10}
                  className="abha-link-btn"
                  style={{
                    background: isDark ? '#0369a1' : '#0284c7',
                    opacity: linkSearching || mobileInput.length < 10 ? 0.6 : 1,
                    cursor: linkSearching || mobileInput.length < 10 ? 'not-allowed' : 'pointer',
                  }}
                >
                  {linkSearching ? 'Searching…' : 'Find Patient'}
                </button>
              </div>

              {linkError && (
                <p className="abha-link-error" style={{ color: isDark ? '#f87171' : '#dc2626' }}>
                  {linkError}
                </p>
              )}

              {/* No results */}
              {linkPatients !== null && linkPatients.length === 0 && (
                <p style={{ fontSize: 14, color: isDark ? '#9ca3af' : '#6b7280', marginTop: 12 }}>
                  No patient found with this mobile number.
                </p>
              )}

              {/* Multiple results — picker */}
              {linkPatients !== null && linkPatients.length > 1 && !selectedPatient && (
                <div style={{ marginTop: 12 }}>
                  <p style={{ fontSize: 13, color: isDark ? '#9ca3af' : '#6b7280', marginBottom: 8 }}>
                    Multiple patients found — select one:
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {linkPatients.map((p) => (
                      <button
                        key={p.patient_id}
                        onClick={() => fetchABHARecord(p)}
                        className="abha-patient-row"
                        style={{
                          background: isDark ? '#1e2d3d' : '#f0f9ff',
                          border: `1px solid ${isDark ? '#1e40af' : '#bae6fd'}`,
                          color: isDark ? '#f1f5f9' : '#111827',
                        }}
                      >
                        {`${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown'}
                        <span style={{ color: isDark ? '#94a3b8' : '#6b7280', fontWeight: 400, marginLeft: 6 }}>
                          ({p.mobile || mobileInput})
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Loading record */}
              {linkLoading && (
                <p style={{ fontSize: 14, color: isDark ? '#9ca3af' : '#6b7280', marginTop: 12 }}>
                  Checking ABHA status…
                </p>
              )}

              {/* ABHA record result */}
              {abhaRecord && selectedPatient && (
                <div
                  className="abha-record-card"
                  style={{
                    background: abhaRecord.hasAbha
                      ? (isDark ? 'rgba(5,150,105,0.12)' : '#f0fdf4')
                      : (isDark ? 'rgba(234,88,12,0.10)' : '#fff7ed'),
                    border: `1px solid ${abhaRecord.hasAbha
                      ? (isDark ? '#065f46' : '#bbf7d0')
                      : (isDark ? '#9a3412' : '#fdba74')}`,
                  }}
                >
                  <p className="abha-record-name" style={{ color: isDark ? '#f1f5f9' : '#111827' }}>
                    {`${selectedPatient.first_name || ''} ${selectedPatient.last_name || ''}`.trim() || 'Patient'}
                  </p>

                  {abhaRecord.hasAbha ? (
                    <>
                      <p style={{ margin: '0 0 6px' }}>
                        <AbhaBadge status={abhaRecord.abhaStatus} isDark={isDark} />
                      </p>
                      {abhaRecord.abhaAddress && (
                        <p style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#6b7280', margin: '0 0 2px' }}>
                          {ABHA_ADDRESS_LABEL}&nbsp;
                          <span style={{ color: isDark ? '#f1f5f9' : '#111827', fontWeight: 500 }}>{maskAbhaAddress(abhaRecord.abhaAddress)}</span>
                        </p>
                      )}
                      {abhaRecord.abhaNumberMasked && (
                        <p style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#6b7280', margin: 0 }}>
                          {ABHA_NUMBER_LABEL}&nbsp;
                          <span style={{ color: isDark ? '#f1f5f9' : '#111827', fontWeight: 500 }}>{abhaRecord.abhaNumberMasked}</span>
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p style={{ fontSize: 13, color: isDark ? '#fb923c' : '#ea580c', fontWeight: 600, margin: '0 0 10px' }}>
                        {ABHA_PAGE.noAbhaOnFile}
                      </p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {canVerify && (
                          <button
                            onClick={() => openCardTab('verify')}
                            className="abha-link-btn"
                            style={{ background: isDark ? '#0369a1' : '#0284c7', cursor: 'pointer' }}
                          >
                            {VERIFY_ABHA}
                          </button>
                        )}
                        {canCreate && (
                          <button
                            onClick={() => openCardTab('create')}
                            className="abha-link-btn"
                            style={{ background: isDark ? '#0369a1' : '#0284c7', cursor: 'pointer' }}
                          >
                            {CREATE_ABHA}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
